<template>
	<div class="sideways-content-fold" v-if="activePanel">
		<div class="portlet light bordered">
			<div class="portlet-title">
				<div class="caption">
					Manage
				</div>
			</div>
			<div class="portlet-body portlet-overflow">
				<form role="form" @submit.prevent="updateFolder($event)" novalidate>
					<div class="alert alert-danger" v-if="errorMessage.length">
					    <button class="close" data-close="alert" @click="clearError()"></button>
                        <span>{{ errorMessage }}</span>
					</div>
					<transition name="fade" mode="out-in">
						<div>
							<div class="form-body invite-user-form">
								<div class="form-group form-md-line-input form-md-floating-label">
		                            <input type="text" class="form-control input-sm" :class="{'edited': editingFolder.name.length}" id="manage_folder_name" v-model.trim="editingFolder.name" maxlength="255" :disabled="rootFolder">
		                            <label for="manage_folder_name">Folder Name</label>
		                        </div>
		                        <div class="form-md-checkboxes margin-bottom-20" v-if="!activeLocationId">
		                        	<div class="md-checkbox-inline">
		                        		<div class="md-checkbox">
		                        			<input type="checkbox" id="checkbox-privatize" class="md-check" v-model="editingFolder.is_shared">
		                        			<label for="checkbox-privatize">
		                        				<span></span>
		                        				<span class="check"></span>
		                        				<span class="box"></span>
		                        				Make this folder <b>public</b> to all locations.
		                        			</label>
		                        		</div>
		                        	</div>
		                        </div>
							</div>
							<div class="form-actions noborder invite-user-actions">
								<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To Resources Dashboard</button>
								<button type="submit" class="btn blue">Submit</button>
							</div>
						</div>
					</transition>
				</form>
			</div>
		</div>
	</div>
</template>
<script>
import GlobalFunctions from '../../../global'
import Resources from '../../../controllers/Resources'
import { mapGetters } from 'vuex'

/**
 * Manage Folder module is a page for managing a resource folder.
 * @class ManageFolder
 * @extends Resources
 * @since 0.0.9
 */

export default {
	name: 'manage-folder',
	data () {
		return {
			activePanel: false,
			errorMessage: '',
			editingFolder: {
				name: '',
				is_shared: false
			},
			folderId: 0,
			rootFolder: false
		}
	},
	/**
	 * Run on created to determine if this should be the active panel or not.
	 * @function
	 * @return {undefined}
	 * @memberof ManageFolder
	 * @version 0.0.9
	 */
	created () {
		if (this.$route.name === 'Resources - Manage Folder') {
			this.activePanel = true
			if (this.$route.params.folder_id && this.$route.params.folder_id !== 'undefined' && parseInt(this.$route.params.folder_id) !== 0) {
				this.folderId = this.$route.params.folder_id
				this.getFolder()
			}
		} else {
			this.activePanel = false
		}
	},
	computed: mapGetters([
		'activeBusinessId',
		'activeBusinessName',
		'activeLocationId'
	]),
	methods: {
		/**
		 * To clear the current error.
		 * @function
		 * @returns {undefined}
		 * @memberof ManageFolder
		 * @version 0.0.9
		 */
		clearError () {
			this.errorMessage = ''
		},
		/**
		 * To close the manage folder panel by redirecting to the dashboard
		 * @function
		 * @returns {undefined}
		 * @memberof ManageFolder
		 * @version 0.0.9
		 */
		closePanel () {
			this.$router.push('/app/resources')
		},
		/**
		 * To get details for the current folder.
		 * @function
		 * @return {undefined}
		 * @memberof ManageFolder
		 * @version 0.0.9
		 */
		getFolder () {
			let _this = this
			const businessId = this.activeBusinessId
			const locationId = this.activeLocationId

			return Resources.getFolder(businessId, locationId, _this.folderId).then(response => {
				_this.editingFolder = Object.assign({}, response.payload)

				if (_this.editingFolder.name === 'root') {
					_this.rootFolder = true
					_this.editingFolder.name = `${_this.activeBusinessName} Resources`
				} else {
					_this.rootFolder = false
				}
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To validate that all information is accurate before allowing the user to proceed.
		 * @function
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof ManageFolder
		 * @version 0.0.9
		 */
		validateFormData () {
			let _this = this

			return new Promise((resolve, reject) => {
				if (!_this.editingFolder.name.length) {
					reject('Folder name cannot be blank.')
				}

				resolve()
			})
		},
		/**
		 * To send the updated details of the folder to the backend.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof ManageFolder
		 * @version 0.0.9
		 */
		updateFolder (event) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this
			const businessId = this.activeBusinessId
			const locationId = this.activeLocationId

			return _this.validateFormData().then(response => {
				_this.clearError()
				let folderName = _this.rootFolder ? 'root' : _this.editingFolder.name
				Resources.updateFolder(businessId, locationId, _this.editingFolder.id, folderName, _this.editingFolder.is_shared).then(response => {
					_this.closePanel()
					disabledButton.complete()
				}).catch(_this.$root.errorWrapper(e => {
					if (e.responseJSON) {
						switch (e.responseJSON.declaration) {
						default:
							// No default
						}
					}
				}))
			}).catch(reason => {
				// If validation fails then display the error message
				_this.errorMessage = reason
				disabledButton.cancel()
			})
		}
	},
	watch: {
		/**
		 * To `watch` the $route in order to determine if this panel should be showing or not
		 * @function
		 * @returns {undefined}
		 * @memberof ManageFolder
		 * @version 0.0.9
		 */
		'$route' () {
			if (this.$route.name === 'Resources - Manage Folder') {
				this.activePanel = true
				this.clearError()

				if (this.$route.params.folder_id && this.$route.params.folder_id !== 'undefined' && parseInt(this.$route.params.folder_id) !== 0) {
					this.folderId = this.$route.params.folder_id
					this.getFolder()
				} else {
					this.closePanel()
				}
			} else {
				this.activePanel = false
			}
		}
	}
}
</script>



// WEBPACK FOOTER //
// ManageFolder.vue?28305da8